import { useEffect, useRef, useState } from 'preact/hooks';
import { LineChart, PieChart, type LineSeriesOption, type PieSeriesOption } from 'echarts/charts';
import {
  AriaComponent,
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent,
  type AriaComponentOption,
  type GridComponentOption,
  type LegendComponentOption,
  type TitleComponentOption,
  type TooltipComponentOption,
} from 'echarts/components';
import {
  init,
  use,
  type ComposeOption,
  type EChartsType,
} from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';

use([
  LineChart,
  PieChart,
  AriaComponent,
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent,
  CanvasRenderer,
]);

export type AppEChartOption = ComposeOption<
  | LineSeriesOption
  | PieSeriesOption
  | AriaComponentOption
  | GridComponentOption
  | LegendComponentOption
  | TitleComponentOption
  | TooltipComponentOption
>;

interface EChartProps {
  option: AppEChartOption;
  ariaLabel: string;
  className?: string;
  loading?: boolean;
  empty?: boolean;
  emptyMessage?: string;
}

export function EChart({
  option,
  ariaLabel,
  className = '',
  loading = false,
  empty = false,
  emptyMessage = 'No chart data available',
}: EChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<EChartsType | null>(null);
  const [initializationFailed, setInitializationFailed] = useState(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    try {
      const chart = init(container, undefined, { renderer: 'canvas' });
      chartRef.current = chart;

      const resizeObserver = new ResizeObserver(() => chart.resize());
      resizeObserver.observe(container);

      return () => {
        resizeObserver.disconnect();
        chart.dispose();
        chartRef.current = null;
      };
    } catch (error) {
      console.error('Unable to initialize ECharts:', error);
      setInitializationFailed(true);
      return undefined;
    }
  }, []);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;

    if (loading || empty) {
      chart.clear();
      return;
    }

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    chart.setOption(
      reduceMotion ? { ...option, animation: false } : option,
      { notMerge: true, lazyUpdate: false },
    );
    chart.resize();
  }, [option, loading, empty]);

  const statusMessage = initializationFailed
    ? 'Unable to display chart'
    : loading
      ? 'Loading…'
      : empty
        ? emptyMessage
        : null;

  return (
    <div className={`echart ${className}`.trim()}>
      <div
        ref={containerRef}
        className="echart__canvas"
        role="img"
        aria-label={ariaLabel}
      />
      {statusMessage && (
        <div className="echart__status" role="status">
          {statusMessage}
        </div>
      )}
    </div>
  );
}
